import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDocument, createDocument } from '../hooks/useSupabase';
import toast from 'react-hot-toast';

const RequestViewPage = () => {
  const { requestId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    canDonate: true
  });

  const { data: request, loading, error } = useDocument('blood_requests', requestId);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Request Not Found</h2>
          <p className="text-gray-600 mb-4">This blood request may have been fulfilled or removed.</p>
          <Link to="/" className="text-red-600 font-medium hover:underline">
            Go to Home
          </Link>
        </div>
      </div>
    );
  }

  const isActive = request.status === 'active';

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save response to database
      const responseData = {
        request_id: requestId,
        responder_name: donorInfo.name,
        responder_phone: donorInfo.phone,
        responder_email: donorInfo.email || null,
        can_donate: donorInfo.canDonate,
        message: donorInfo.message || null,
        status: 'pending'
      };
      
      await createDocument('donor_responses', responseData, true, true); // retry=true, isPublic=true
      
      toast.success(
        donorInfo.canDonate 
          ? 'Thank you! Your interest has been recorded. The admin will contact you shortly.'
          : 'Thank you for your response. We appreciate you letting us know.'
      );
      setShowResponseForm(false);
    } catch (err) {
      console.error('Error saving response:', err);
      const errorMsg = err?.message || err?.error || 'Unknown error';
      toast.error(`Failed to submit: ${errorMsg}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" className="text-red-600 font-bold text-lg">
            🩸 OCYM Blood Donation
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Request Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Status Banner */}
          <div className={`px-6 py-3 ${isActive ? 'bg-red-500' : 'bg-green-500'} text-white`}>
            <p className="font-semibold text-center">
              {isActive ? '🚨 URGENT: Active Blood Request' : '✅ This request has been fulfilled'}
            </p>
          </div>

          <div className="p-6">
            {/* Blood Group */}
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center border-4 border-red-200">
                <span className="text-3xl font-bold text-red-700">{request.blood_group}</span>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🏥</span>
                <div>
                  <p className="text-sm text-gray-500">Hospital</p>
                  <p className="font-semibold text-gray-900">
                    {request.hospital?.name || request.hospital_name || 'Not specified'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {request.hospital?.location || request.hospital_location || request.location}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-2xl">👤</span>
                <div>
                  <p className="text-sm text-gray-500">Patient</p>
                  <p className="font-semibold text-gray-900">{request.patient_name || 'Anonymous'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-2xl">🩸</span>
                <div>
                  <p className="text-sm text-gray-500">Blood Group Needed</p>
                  <p className="font-semibold text-gray-900">{request.blood_group}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-2xl">📅</span>
                <div>
                  <p className="text-sm text-gray-500">Required By</p>
                  <p className="font-semibold text-gray-900">
                    {request.required_by 
                      ? new Date(request.required_by).toLocaleDateString('en-IN', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })
                      : 'As soon as possible'}
                  </p>
                </div>
              </div>

              {request.units_required && (
                <div className="flex items-start gap-3">
                  <span className="text-2xl">💉</span>
                  <div>
                    <p className="text-sm text-gray-500">Units Required</p>
                    <p className="font-semibold text-gray-900">{request.units_required} units</p>
                  </div>
                </div>
              )}

              {request.notes && (
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📝</span>
                  <div>
                    <p className="text-sm text-gray-500">Additional Notes</p>
                    <p className="text-gray-700">{request.notes}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Response Section */}
            {isActive ? (
              <div className="border-t border-gray-100 pt-6">
                {!showResponseForm ? (
                  <div className="text-center">
                    <p className="text-gray-600 mb-4">
                      Can you help with this blood donation request?
                    </p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => { setDonorInfo(prev => ({ ...prev, canDonate: true })); setShowResponseForm(true); }}
                        className="px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 active:bg-green-700 transition-colors"
                      >
                        ✅ Yes, I Can Donate
                      </button>
                      <button
                        onClick={() => { setDonorInfo(prev => ({ ...prev, canDonate: false })); setShowResponseForm(true); }}
                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                      >
                        ❌ Not Available
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmitResponse} className="space-y-4">
                    <h3 className="font-bold text-lg text-center">
                      {donorInfo.canDonate ? '🙏 Thank you for volunteering!' : 'Thank you for letting us know'}
                    </h3>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                      <input
                        type="text"
                        required
                        value={donorInfo.name}
                        onChange={(e) => setDonorInfo(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter your name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        required
                        value={donorInfo.phone}
                        onChange={(e) => setDonorInfo(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter your phone number"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
                      <input
                        type="email"
                        value={donorInfo.email}
                        onChange={(e) => setDonorInfo(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Enter your email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                      <textarea
                        value={donorInfo.message}
                        onChange={(e) => setDonorInfo(prev => ({ ...prev, message: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="Any additional information..."
                        rows={3}
                      />
                    </div>

                    {donorInfo.canDonate && (
                      <div className="bg-green-50 p-4 rounded-xl">
                        <p className="text-sm text-green-800">
                          <span className="font-semibold">Next Steps:</span><br/>
                          1. An admin will contact you to confirm<br/>
                          2. Please visit the hospital at the scheduled time<br/>
                          3. Bring a valid ID proof
                        </p>
                      </div>
                    )}

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowResponseForm(false)}
                        className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50"
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Response'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="text-5xl mb-2">🎉</div>
                <p className="text-green-600 font-semibold">This request has been fulfilled!</p>
                <p className="text-gray-500 text-sm mt-1">
                  Thank you to all the donors who responded.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Want to register as a blood donor?{' '}
            <Link to="/register" className="text-red-600 font-medium hover:underline">
              Sign up here
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default RequestViewPage;
